import React, { useState, useEffect } from "react";
import axios from "axios";
import { Spin, Typography, message } from "antd";
import { SearchOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { ModalItem, ButtonItem, InputItem } from "../../../../../Components/Items";
import TextAreaItem from "../../../../../Components/TextAreaItem";
import { WA } from "../../theme";
import { parseGoogleMapsUrl, bboxDe } from "../Helpers/ubicacionesHelpers";

const { Text } = Typography;

// Crea/edita una ubicación por defecto. `ubicacion` null = crear.
const EditorUbicacion = ({ visible, headersToken, empresaId, ubicacion, onCancel, onGuardado }) => {
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [direccion, setDireccion] = useState("");
  const [googleMapsUrl, setGoogleMapsUrl] = useState("");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");

  const [query, setQuery] = useState("");
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const esEdicion = !!ubicacion;

  useEffect(() => {
    if (!visible) return;
    if (ubicacion) {
      setNombre(ubicacion.nombre || "");
      setDescripcion(ubicacion.descripcion || "");
      setDireccion(ubicacion.direccion || "");
      setGoogleMapsUrl(ubicacion.googleMapsUrl || "");
      setLat(ubicacion.latitude != null ? String(ubicacion.latitude) : "");
      setLng(ubicacion.longitude != null ? String(ubicacion.longitude) : "");
    } else {
      setNombre("");
      setDescripcion("");
      setDireccion("");
      setGoogleMapsUrl("");
      setLat("");
      setLng("");
    }
    setQuery("");
    setResultados([]);
  }, [visible, ubicacion]);

  // Al pegar/cambiar el link de Google Maps, intenta extraer las coordenadas.
  const cambiarLink = (valor) => {
    setGoogleMapsUrl(valor);
    if (!valor.trim()) return;
    const coords = parseGoogleMapsUrl(valor);
    if (coords) {
      setLat(String(coords.lat));
      setLng(String(coords.lng));
      message.success("Coordenadas detectadas del link.");
    } else {
      message.warning(
        "No se detectaron coordenadas en el link. Si es un link corto, ábrelo en el navegador y copia el URL completo, o usa el buscador o lat/lng manual.",
      );
    }
  };

  const buscar = async () => {
    if (!query.trim()) return;
    setBuscando(true);
    try {
      const resp = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=6&q=${encodeURIComponent(query)}`,
      );
      setResultados(await resp.json());
    } catch {
      message.error("No se pudo buscar la dirección.");
    } finally {
      setBuscando(false);
    }
  };

  const elegir = (r) => {
    setLat(String(parseFloat(r.lat)));
    setLng(String(parseFloat(r.lon)));
    setDireccion(r.display_name || "");
    if (!nombre.trim()) setNombre((r.display_name || "").split(",")[0] || "");
    setResultados([]);
    setQuery("");
  };

  const guardar = () => {
    if (!nombre.trim()) return message.error("Ponle un nombre a la ubicación.");
    const latN = parseFloat(lat);
    const lngN = parseFloat(lng);
    if (!Number.isFinite(latN) || !Number.isFinite(lngN)) {
      return message.error("Faltan las coordenadas. Pega un link, busca o escribe lat/lng.");
    }
    if (latN < -90 || latN > 90 || lngN < -180 || lngN > 180) {
      return message.error("Las coordenadas están fuera de rango.");
    }

    const endpoint = esEdicion ? "whatsapp/ubicacionEditar" : "whatsapp/ubicacionCrear";
    const body = {
      nombre: nombre.trim(),
      descripcion: descripcion.trim(),
      direccion: direccion.trim(),
      latitude: latN,
      longitude: lngN,
      googleMapsUrl: googleMapsUrl.trim(),
      ...(esEdicion ? { ubicacionId: ubicacion._id } : { empresaId }),
    };

    setGuardando(true);
    axios
      .post(endpoint, body, { headers: headersToken })
      .then((r) => {
        if (r.data.success === 2) {
          message.success(esEdicion ? "Ubicación actualizada." : "Ubicación creada.");
          onGuardado();
        } else {
          message.error(r.data.message || "No se pudo guardar.");
        }
      })
      .catch(() => message.error("Error de red al guardar."))
      .finally(() => setGuardando(false));
  };

  const tituloSeccion = { fontWeight: 600, margin: "14px 0 4px", color: WA.textPrimary };
  const hayCoords = Number.isFinite(parseFloat(lat)) && Number.isFinite(parseFloat(lng));

  return (
    <ModalItem
      title={esEdicion ? "Editar ubicación" : "Nueva ubicación"}
      visible={visible}
      onCancel={onCancel}
      width={560}
      zIndex={1200}
      destroyOnClose
      spinning={guardando}
    >
      <div style={tituloSeccion}>Nombre</div>
      <InputItem
        placeholder="Ej. Sucursal Centro"
        value={nombre}
        onChange={(e) => setNombre(e.target.value)}
        maxLength={60}
      />

      <div style={tituloSeccion}>Descripción</div>
      <TextAreaItem
        placeholder="Ej. Frente al parque, piso 1"
        value={descripcion}
        onChange={(e) => setDescripcion(e.target.value)}
        rows={2}
      />

      <div style={tituloSeccion}>Link de Google Maps</div>
      <InputItem
        placeholder="Pega aquí el link de Google Maps"
        value={googleMapsUrl}
        onChange={(e) => cambiarLink(e.target.value)}
      />
      <div style={{ fontSize: 11, color: WA.textSecondary, marginTop: 4 }}>
        Copia el link de la <b>barra del navegador</b> (el que tiene <code>/@...</code> en la
        dirección). El botón <b>"Compartir"</b> da un link corto que <b>no</b> incluye las
        coordenadas.
      </div>

      <div style={tituloSeccion}>Buscar dirección</div>
      <div style={{ display: "flex", gap: 8 }}>
        <InputItem
          placeholder="Buscar dirección o lugar..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onPressEnter={buscar}
        />
        <ButtonItem cyan icon={<SearchOutlined />} onClick={buscar} />
      </div>
      <div style={{ fontSize: 11, color: WA.textSecondary, marginTop: 4 }}>
        El buscador puede ser <b>impreciso</b>. Para mayor exactitud usa el link de Google Maps y
        verifica el pin en el mapa de abajo.
      </div>

      {buscando && (
        <div style={{ textAlign: "center", padding: 10 }}>
          <Spin size="small" />
        </div>
      )}

      {resultados.length > 0 && (
        <div
          style={{
            marginTop: 8,
            maxHeight: 150,
            overflowY: "auto",
            border: `1px solid ${WA.border}`,
            borderRadius: 8,
          }}
        >
          {resultados.map((r) => (
            <div
              key={r.place_id}
              onClick={() => elegir(r)}
              style={{ padding: "8px 10px", cursor: "pointer", borderBottom: `1px solid ${WA.border}` }}
            >
              <EnvironmentOutlined style={{ color: WA.naranja, marginRight: 6 }} />
              <Text style={{ fontSize: 13 }}>{r.display_name}</Text>
            </div>
          ))}
        </div>
      )}

      <div style={tituloSeccion}>Coordenadas</div>
      <div style={{ display: "flex", gap: 8 }}>
        <InputItem
          placeholder="Latitud"
          value={lat}
          onChange={(e) => setLat(e.target.value)}
        />
        <InputItem
          placeholder="Longitud"
          value={lng}
          onChange={(e) => setLng(e.target.value)}
        />
      </div>

      {hayCoords && (
        <div style={{ marginTop: 12 }}>
          <iframe
            title="mapa-preview-editor"
            src={`https://www.openstreetmap.org/export/embed.html?bbox=${bboxDe(
              parseFloat(lat),
              parseFloat(lng),
            )}&layer=mapnik&marker=${parseFloat(lat)},${parseFloat(lng)}`}
            style={{ width: "100%", height: 170, border: "none", borderRadius: 8 }}
          />
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
        <ButtonItem rojo onClick={onCancel}>Cancelar</ButtonItem>
        <ButtonItem lima onClick={guardar} loading={guardando}>Guardar</ButtonItem>
      </div>
    </ModalItem>
  );
};

export default EditorUbicacion;
